import React, { ComponentType } from 'react';
import { useRoles } from '../../context/RolesProvider';

interface WithRoleProtectionOptions {
  requiredRoles?: string[];
  fallbackComponent?: ComponentType;
  loadingComponent?: ComponentType;
}

const withRoleProtection = <P extends object>(
  WrappedComponent: ComponentType<P>,
  options: WithRoleProtectionOptions = {},
) => {
  const {
    requiredRoles = [],
    fallbackComponent: FallbackComponent,
    loadingComponent: LoadingComponent,
  } = options;

  const RoleProtectedComponent: React.FC<P> = props => {
    const { isLoading, error, hasAnyRole, isAdmin } = useRoles();

    if (isLoading) {
      return LoadingComponent ? <LoadingComponent /> : <div>Loading...</div>;
    }

    if (error) {
      return <div>Error loading user permissions: {error}</div>;
    }

    // Grant access if user is an admin or has any of the required roles
    const hasAccess = isAdmin || requiredRoles.length === 0 || hasAnyRole(requiredRoles);

    if (!hasAccess) {
      return FallbackComponent ? (
        <FallbackComponent />
      ) : (
        <div>You do not have access to this page.</div>
      );
    }

    return <WrappedComponent {...props} />;
  };

  RoleProtectedComponent.displayName = `withRoleProtection(${
    WrappedComponent.displayName || WrappedComponent.name
  })`;

  return RoleProtectedComponent;
};

export default withRoleProtection;
